import * as React from 'react';
import axios from 'axios';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import HelpIcon from '@material-ui/icons/HelpOutlineRounded';
import Tooltip from '@material-ui/core/Tooltip';

import Button from '../_common/button';
import Notification from '../_common/notification';

import getTranslatedText from './getTranslatedText';
import NerdMentions from './nerd-mentions';
import EntitiesFound from './entities-found';
import generateEntityColorMap from './generateEntityColorMap';
import { ErrorManager } from '../../utilities/error-manager';
import getConfig from '../../helpers/get-config';

const urls = getConfig('urls');

const NERD_EXAMPLES: { descriptionKey: string; text: string; knowledgeBase: KnowledgeBase }[] = [
    {
        descriptionKey: 'example1',
        text:
            'These interim Centers for Disease Control and Prevention clinical considerations are informed by data submitted to the Food and Drug Administration (FDA) for Emergency Use Authorization of the vaccines, other data sources, general best practice guidelines for immunization, and expert opinion. These considerations apply only to the vaccine products currently authorized in the United States (i.e., Pfizer, Moderna, and Johnson & Johnson vaccines).',
        knowledgeBase: 'capiq'
    },
    {
        descriptionKey: 'example2',
        text:
            'RBS Securities Japan Limited, a wholly owned subsidiary of The Royal Bank of Scotland (RBS), has agreed to plead guilty to felony wire fraud and admit its role in manipulating a leading benchmark used in financial products and transactions around the world, the Justice Department and FBI announced today.',
        knowledgeBase: 'capiq'
    },
    {
        descriptionKey: 'example3',
        text:
            "Nairobi denied a request by Supreme Court judges for extra security after one of their bodyguards was shot, preventing them from holding a hearing that could have delayed last week's presidential election. The court has been center stage since it annulled an Aug. 8 vote won by President Uhuru Kenyatta, citing procedural irregularities and ordering a repeat vote within 60 days. The decision was the first of its kind in Africa, where governments often hold sway over judges. It angered some of Kenyatta's supporters, who said the judges had thwarted the will of the people. Kenyatta won 98 percent of the vote in the Oct. 26 re-run after opposition leader Raila Odinga boycotted it on the grounds it would not be fair because the election board had failed to implement reforms he sought after the court decision, overseen by Chief Justice David Maraga.",
        knowledgeBase: 'wikimedia'
    }
];

const KNOWLEDGE_BASE_OPTIONS: { value: KnowledgeBase; label: string }[] = [
    { value: 'capiq', label: 'Capital IQ' },
    { value: 'wikimedia', label: 'Wikimedia' }
];

const CHAR_COUNT_MAX = 2000;

function filterAnnotations(annotations: ApiAnnotation[], kb: KnowledgeBase): Annotation[] {
    return annotations.filter((annotation): annotation is Annotation => {
        const threshold = kb === 'wikimedia' ? 0.15 : 0.95;
        return annotation.ned_score !== null && annotation.ned_score > threshold;
    });
}

export default function KenshoNerdDemo(props: KenshoNerdDemoProps): JSX.Element {
    const { accessToken, onUnauthorizedResponse, onLoaded } = props;
    const [asyncResults, setAsyncResults] = React.useState<AsyncValue<NerdMentions> | null>(null);
    const [text, setText] = React.useState('');
    const [sourceId, setSourceId] = React.useState('');
    const [knowledgeBase, setKnowledgeBase] = React.useState<KnowledgeBase>('capiq');
    const [showCharCountError, setShowCharCountError] = React.useState(false);
    const [showNoEntitiesFoundError, setShowNoEntitiesFoundError] = React.useState<KnowledgeBase | null>(null);
    const [hoveredEntity, setHoveredEntity] = React.useState<string | null>(null);

    function resetDemo(): void {
        setAsyncResults(null);
        setText('');
        setSourceId('');
        setKnowledgeBase('capiq');
        setShowCharCountError(false);
        setHoveredEntity(null);
        setShowNoEntitiesFoundError(null);
    }

    function handleSourceIdChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        setSourceId(event.target.value);
    }

    function handleDemoTextClick(demoText: string, kb: KnowledgeBase): void {
        setAsyncResults(null);
        setText(demoText);
        setKnowledgeBase(kb);
    }

    function handleTextChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        const nextText = event.target.value;
        setText(nextText);
        if (nextText.length === CHAR_COUNT_MAX) {
            setShowCharCountError(true);
        }
    }

    function handleKnowledgeBaseSelect(event: React.ChangeEvent<HTMLInputElement>): void {
        const nextKnowledgeBase = event.target.value as KnowledgeBase;
        if (nextKnowledgeBase === 'wikimedia') setSourceId('');
        setKnowledgeBase(nextKnowledgeBase);
    }

    function handleEntityHover(nextHoveredEntity: string | null) {
        setHoveredEntity(nextHoveredEntity);
    }

    function tagEntities(): void {
        setAsyncResults({ status: 'pending' });

        const sourceIdToSend = sourceId === '' ? '0' : sourceId;
        // eslint-disable-next-line @typescript-eslint/camelcase
        const originatingEntityId = knowledgeBase === 'wikimedia' ? {} : { originating_entity_id: sourceIdToSend };
        const payload = {
            text,
            // eslint-disable-next-line @typescript-eslint/camelcase
            knowledge_bases: [knowledgeBase],
            ...originatingEntityId
        };

        axios
            .post<NerdResponse>(urls.mi.kensho.nerd, JSON.stringify(payload), {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` }
            })
            .then(response => {
                if (response && response.status === 200) {
                    const results = response.data.results[0];
                    const filteredAnnotations = filterAnnotations(results.annotations, results.knowledge_base);
                    if (filteredAnnotations.length === 0) setShowNoEntitiesFoundError(results.knowledge_base);
                    setAsyncResults({
                        status: 'ready',
                        data: { results: { ...results, annotations: filteredAnnotations }, colorMap: generateEntityColorMap(filteredAnnotations) }
                    });
                }
            })
            .catch(error => {
                let errorMessage = getTranslatedText('generalError');
                if (error) {
                    if (error.response && error.response.status === 403) onUnauthorizedResponse();
                    else if (error.response && error.response.status === 429) errorMessage = getTranslatedText('quotaHit');
                    // Only log unexpected errors to avoid the red error notification toast on expected behavior
                    else ErrorManager.apiError(error, { msg: errorMessage });
                }
                setAsyncResults({ status: 'error', error: errorMessage });
            });
    }

    const isInputDisabled = asyncResults !== null;
    const isTagButtonDisabled = text === '' || asyncResults !== null;
    const isResetButtonDisabled =
        (asyncResults === null && text === '' && sourceId === '') || (asyncResults !== null && asyncResults.status === 'pending');

    React.useEffect(() => {
        if (onLoaded) onLoaded();
    }, []);

    return (
        <div className="nerd-demo">
            {showCharCountError && (
                <Notification
                    onClosed={() => setShowCharCountError(false)}
                    message={getTranslatedText('charCountExceeded')}
                    position={{ horizontal: 'left', vertical: 'bottom' }}
                    variant="warning"
                />
            )}
            {showNoEntitiesFoundError !== null && (
                <Notification
                    onClosed={() => setShowNoEntitiesFoundError(null)}
                    message={getTranslatedText(`${showNoEntitiesFoundError}NoEntitiesFound`)}
                    position={{ horizontal: 'left', vertical: 'bottom' }}
                    variant="warning"
                />
            )}
            <p>{getTranslatedText('introText')}</p>
            <div className="nerd-demo__example-links">
                {NERD_EXAMPLES.map(example => (
                    <Button
                        theme="tertiary"
                        className="nerd-demo__example-link"
                        key={example.descriptionKey}
                        type="button"
                        onClick={() => handleDemoTextClick(example.text, example.knowledgeBase)}
                        text={getTranslatedText(example.descriptionKey)}
                    />
                ))}
            </div>
            <div className="two-column-layout">
                <div className="two-column-layout__col nerd-demo__col">
                    <div className="nerd-demo__section-header">
                        <div className="nerd-demo__section-header-section">
                            <span className="nerd-demo__section-title">{getTranslatedText('knowledgeBase')}</span>
                            <RadioGroup className="nerd-demo__knowledge-base-select" row onChange={handleKnowledgeBaseSelect} value={knowledgeBase}>
                                {KNOWLEDGE_BASE_OPTIONS.map(option => (
                                    <FormControlLabel disabled={isInputDisabled} key={option.value} {...option} control={<Radio />} />
                                ))}
                            </RadioGroup>
                        </div>
                        {knowledgeBase === 'capiq' && (
                            <div className="nerd-demo__source-id-wrapper">
                                <Tooltip title={<span className="nerd-demo__tooltip">{getTranslatedText('sourceIdTooltip')}</span>}>
                                    <HelpIcon />
                                </Tooltip>
                                <label htmlFor="source-id" className="nerd-demo__section-title nerd-demo__source-id">
                                    Source ID:
                                    <input
                                        disabled={isInputDisabled}
                                        id="source-id"
                                        value={sourceId}
                                        onChange={handleSourceIdChange}
                                        placeholder="Capital IQ ID"
                                    />
                                </label>
                            </div>
                        )}
                    </div>
                    {asyncResults === null ? (
                        <textarea
                            className="nerd-demo__textarea"
                            placeholder={getTranslatedText('textareaPlaceholder')}
                            value={text}
                            onChange={handleTextChange}
                            rows={15}
                            maxLength={CHAR_COUNT_MAX}
                        />
                    ) : (
                        <div className="nerd-demo__results">
                            <NerdMentions results={asyncResults} text={text} onHover={handleEntityHover} hoveredEntity={hoveredEntity} />
                        </div>
                    )}
                </div>
                <div className="two-column-layout__col nerd-demo__col">
                    <div className="nerd-demo__section-header">
                        <span className="nerd-demo__section-title">{getTranslatedText('entitiesFound')}</span>
                    </div>
                    {asyncResults !== null && asyncResults.status === 'ready' && (
                        <EntitiesFound results={asyncResults.data} onHover={handleEntityHover} hoveredEntity={hoveredEntity} />
                    )}
                </div>
            </div>
            <div className="nerd-demo__action-buttons">
                <Button
                    theme="primary"
                    type="button"
                    onClick={tagEntities}
                    isDisabled={isTagButtonDisabled}
                    text={getTranslatedText('tagEntities')}
                />
                <Button
                    theme="tertiary"
                    className="nerd-demo__reset-button"
                    type="button"
                    onClick={resetDemo}
                    isDisabled={isResetButtonDisabled}
                    text={getTranslatedText('reset')}
                />
            </div>
            <p>
                <span className="nerd-demo__footerNote">{getTranslatedText('footerNote')}</span>
                {getTranslatedText('footerNoteText')}
            </p>
        </div>
    );
}
